import React from 'react';
import PropTypes from 'prop-types';
import stringUtils from '../../qti/utils/StringUtils';
import constants from '../../qti/Constants';
import pciService from '../../services/questionView/PCIService';
import { t } from '../../services/i18nService';
import styles from './pciResourceOverlay.scss';

class PciResourceRuntimeHost extends React.PureComponent {
    static propTypes = {
        runtime: PropTypes.object.isRequired,
        title: PropTypes.string,
        onLoadError: PropTypes.func,
    };

    static defaultProps = {
        title: '',
    };

    constructor(props) {
        super(props);
        this.interactionUID = stringUtils.getUID();
        this.sandboxKey = stringUtils.getUID();
        this.sandbox = null;
        this.state = { loadError: false, ready: false };
    }

    componentDidMount() {
        window.addEventListener('message', this.receivePCIMessage, false);
    }

    componentWillUnmount() {
        if (this.sandbox?.contentWindow) {
            pciService.postMessageToWindow(this.sandbox, this.interactionUID, {
                type: constants.pciMessage.DO_COMPLETED,
            });
        }
        window.removeEventListener('message', this.receivePCIMessage, false);
    }

    checkMessageComesFromMyPCI(event) {
        const { data, origin, source } = event;
        if (!data || typeof data !== 'object') return false;
        if (!this.sandbox?.contentWindow || source !== this.sandbox.contentWindow) return false;
        return location.origin === origin && data.interactionUID === this.interactionUID;
    }

    receivePCIMessage = (event) => {
        const { data } = event;
        if (!this.checkMessageComesFromMyPCI(event)) return;

        switch (data.type) {
        case constants.pciMessage.MODULE_LOADED:
            this.sendMessageToInstantiateAndRenderPCI();
            break;
        case constants.pciMessage.ON_READY:
            this.setState({ ready: true });
            break;
        case constants.pciMessage.MODULE_LOAD_ERROR:
            this.setState({ loadError: true });
            if (this.props.onLoadError) {
                this.props.onLoadError();
            }
            break;
        default:
            break;
        }
    };

    // TEMP DEMO WORKAROUND — backend response is currently missing mainModule, markup,
    // and typeIdentifier in pciRuntime (see IB-19361). Remove these fallbacks once the
    // backend extends buildPciRuntimeFromTemplate to include the tool-level fields.
    getPciRuntimeFields() {
        const { runtime } = this.props;
        const pciRuntime = runtime.pciRuntime || {};
        const resource = runtime.pciResource || {};
        return {
            ...pciRuntime,
            mainModule: pciRuntime.mainModule || 'pciTool',
            markup: pciRuntime.markup || '<div id="pciInteraction"></div>',
            typeIdentifier: pciRuntime.typeIdentifier
                || `content_preset_pci_template_${resource.contentItemId}_${resource.contentRevisionId}`,
        };
    }

    sendMessageToInstantiateAndRenderPCI() {
        const pciRuntime = this.getPciRuntimeFields();
        pciService.postMessageToWindow(this.sandbox, this.interactionUID, {
            type: constants.pciMessage.GET_INSTANCE,
            typeIdentifier: pciRuntime.typeIdentifier,
            configuration: {
                properties: pciRuntime.properties || {},
                templateVariables: pciRuntime.templateVariables || {},
                boundTo: pciRuntime.boundTo || {},
                status: pciRuntime.status || constants.itemSessionStatuses.INITIAL,
            },
            state: pciRuntime.state || null,
        });
    }

    onIFrameLoaded = () => {
        const { runtime } = this.props;
        const pciRuntime = this.getPciRuntimeFields();
        const config = Object.assign({}, runtime.pciModuleConfiguration, {
            doNotAppendExtIfProtocolIsPresent: true,
        });
        pciService.postMessageToWindow(this.sandbox, this.interactionUID, {
            type: constants.pciMessage.INIT,
            pciModuleConfiguration: config,
            mainModule: pciRuntime.mainModule,
            markup: pciRuntime.markup,
        });
    };

    onEmptySandboxRendered = (sandBox) => {
        if (!sandBox) return;
        const { runtime } = this.props;
        const config = Object.assign({}, runtime.pciModuleConfiguration, {
            doNotAppendExtIfProtocolIsPresent: true,
        });
        const content = pciService.buildSandboxHTML(
            this.interactionUID,
            config.cssPath || '',
            [constants.staticLibPaths.REQUIREJS, constants.staticLibPaths.PCI_COMMUNICATION_BRIDGE],
            runtime.pciRuntime?.language || 'en'
        );
        this.sandbox = sandBox;
        pciService.writeSandboxContent(sandBox, content, this.onIFrameLoaded);
    };

    render() {
        const { title } = this.props;
        const { loadError, ready } = this.state;

        if (loadError) {
            return (
                <div className={styles.pciOverlayStatus}>
                    <i className="fa fa-exclamation-circle" aria-hidden="true" />
                    <span>{t('general.failedToLoadResource')}</span>
                </div>
            );
        }

        return (
            <React.Fragment>
                {!ready && (
                    <div className={styles.pciOverlayStatus}>
                        <i className="fa fa-spinner fa-spin" aria-hidden="true" />
                        <span>{t('multimedia.actionButton.loading')}</span>
                    </div>
                )}
                <iframe
                    key={this.sandboxKey}
                    title={title || 'PCI resource'}
                    ref={this.onEmptySandboxRendered}
                    src="about:blank"
                    className={styles.pciSandbox}
                    style={ready ? undefined : { visibility: 'hidden', position: 'absolute' }}
                />
            </React.Fragment>
        );
    }
}

export default PciResourceRuntimeHost;
